// ── Bullet Holes, Smoke & Dust Effects ──

import * as THREE from 'three';
import {
  addEffect, createSpriteTexture, effectTextureCache,
  markEffect, disposeMesh, rand,
} from './effect-loop';

// ---------------------------------------------------------------------------
// Surface Type System
// ---------------------------------------------------------------------------

export enum SurfaceType {
  Concrete = 'concrete',
  Metal = 'metal',
  Wood = 'wood',
  Sand = 'sand',
  Tile = 'tile',
  Glass = 'glass',
}

/**
 * Detect surface type from a Three.js material by checking name and userData.
 * Falls back to Concrete for unknown surfaces.
 */
export function detectSurfaceType(material: THREE.Material): SurfaceType {
  const name = material.name.toLowerCase();
  const ud = material.userData;

  // Check userData.surfaceType first (explicit override)
  if (ud && typeof ud.surfaceType === 'string') {
    const st = ud.surfaceType.toLowerCase();
    if (st in SurfaceType) return st as SurfaceType;
    for (const v of Object.values(SurfaceType)) {
      if (st === v) return v;
    }
  }

  // Heuristic matching by material name
  if (/metal|steel|iron|alum|copper|pipe|vent|duct/i.test(name)) return SurfaceType.Metal;
  if (/wood|plank|board|crate|pallet|log|timber/i.test(name)) return SurfaceType.Wood;
  if (/sand|dirt|gravel|soil|ground/i.test(name)) return SurfaceType.Sand;
  if (/tile|ceramic|porcelain|bathroom|kitchen/i.test(name)) return SurfaceType.Tile;
  if (/glass|window|mirror|transparent/i.test(name)) return SurfaceType.Glass;

  // Default
  return SurfaceType.Concrete;
}

// ---------------------------------------------------------------------------
// Per-Surface Particle Configs
// ---------------------------------------------------------------------------

interface SurfaceParticleConfig {
  colors: [string, string]; // [center, edge] for createSpriteTexture
  count: [number, number]; // [min, max] particle count
  speed: [number, number]; // [min, max] m/s
  blending: THREE.Blending;
  spriteType: 'circle' | 'spark' | 'smoke';
  scaleRange: [number, number]; // initial sprite scale
  scaleGrow: number; // additional scale at t=1
  opacityStart: number;
  gravity: number;
  durationMs: number; // particle lifetime in milliseconds
}

const SURFACE_PARTICLE_CONFIGS: Record<SurfaceType, SurfaceParticleConfig> = {
  [SurfaceType.Concrete]: {
    colors: ['rgba(160,150,130,0.8)', 'rgba(120,110,95,0.3)'],
    count: [3, 5],
    speed: [2, 4],
    blending: THREE.NormalBlending,
    spriteType: 'circle',
    scaleRange: [0.03, 0.06],
    scaleGrow: 0.06,
    opacityStart: 0.7,
    gravity: -15,
    durationMs: 300,
  },
  [SurfaceType.Metal]: {
    colors: ['rgba(255,220,150,1)', 'rgba(255,160,50,0.8)'],
    count: [5, 8],
    speed: [8, 15],
    blending: THREE.AdditiveBlending,
    spriteType: 'spark',
    scaleRange: [0.02, 0.04],
    scaleGrow: -0.01,
    opacityStart: 1.0,
    gravity: -20,
    durationMs: 200,
  },
  [SurfaceType.Wood]: {
    colors: ['rgba(200,170,110,0.9)', 'rgba(160,130,80,0.4)'],
    count: [3, 4],
    speed: [3, 6],
    blending: THREE.NormalBlending,
    spriteType: 'spark',
    scaleRange: [0.02, 0.05],
    scaleGrow: 0.0,
    opacityStart: 0.8,
    gravity: -12,
    durationMs: 300,
  },
  [SurfaceType.Sand]: {
    colors: ['rgba(210,190,130,0.7)', 'rgba(180,160,100,0.3)'],
    count: [4, 6],
    speed: [1, 3],
    blending: THREE.NormalBlending,
    spriteType: 'smoke',
    scaleRange: [0.05, 0.09],
    scaleGrow: 0.1,
    opacityStart: 0.6,
    gravity: -6,
    durationMs: 500,
  },
  [SurfaceType.Tile]: {
    colors: ['rgba(220,215,210,0.9)', 'rgba(180,175,170,0.4)'],
    count: [3, 5],
    speed: [5, 10],
    blending: THREE.NormalBlending,
    spriteType: 'circle',
    scaleRange: [0.02, 0.04],
    scaleGrow: -0.005,
    opacityStart: 0.9,
    gravity: -25,
    durationMs: 300,
  },
  [SurfaceType.Glass]: {
    colors: ['rgba(255,255,255,1)', 'rgba(200,220,255,0.6)'],
    count: [8, 12],
    speed: [4, 8],
    blending: THREE.AdditiveBlending,
    spriteType: 'spark',
    scaleRange: [0.015, 0.035],
    scaleGrow: -0.01,
    opacityStart: 1.0,
    gravity: -18,
    durationMs: 200,
  },
};

// ---------------------------------------------------------------------------
// Per-Surface Bullet Hole Textures (procedural, cached)
// ---------------------------------------------------------------------------

const SURFACE_BULLET_HOLE_COLORS: Record<SurfaceType, { center: string; ring: string; crack: string }> = {
  [SurfaceType.Concrete]: { center: 'rgba(15,12,10,0.95)', ring: 'rgba(80,75,65,0.2)', crack: 'rgba(20,18,15,0.6)' },
  [SurfaceType.Metal]:    { center: 'rgba(10,10,12,0.95)', ring: 'rgba(60,65,70,0.3)', crack: 'rgba(50,55,60,0.7)' },
  [SurfaceType.Wood]:     { center: 'rgba(30,20,10,0.9)',  ring: 'rgba(120,90,50,0.25)', crack: 'rgba(80,55,25,0.5)' },
  [SurfaceType.Sand]:     { center: 'rgba(40,35,20,0.7)',  ring: 'rgba(160,140,90,0.15)', crack: 'rgba(100,85,50,0.3)' },
  [SurfaceType.Tile]:     { center: 'rgba(10,10,10,0.95)', ring: 'rgba(200,195,190,0.2)', crack: 'rgba(150,145,140,0.7)' },
  [SurfaceType.Glass]:    { center: 'rgba(5,5,8,0.9)',     ring: 'rgba(180,200,220,0.15)', crack: 'rgba(200,210,220,0.8)' },
};

function createSurfaceBulletHoleTexture(surface: SurfaceType): THREE.Texture {
  const key = `_bullethole_${surface}_`;
  const cached = effectTextureCache.get(key);
  if (cached) return cached;

  const colors = SURFACE_BULLET_HOLE_COLORS[surface];
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;

  // Dark center hole
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx * 0.9);
  grad.addColorStop(0, colors.center);
  grad.addColorStop(0.2, colors.center.replace(/[\d.]+\)$/, '0.85)'));
  grad.addColorStop(0.35, colors.ring.replace(/[\d.]+\)$/, '0.5)'));
  grad.addColorStop(0.6, colors.ring);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // Crack lines radiating from center
  ctx.strokeStyle = colors.crack;
  ctx.lineWidth = surface === SurfaceType.Glass ? 1.5 : 1;
  const crackCount = surface === SurfaceType.Glass
    ? 8 + Math.floor(Math.random() * 4)  // more cracks for glass
    : 4 + Math.floor(Math.random() * 4);
  for (let i = 0; i < crackCount; i++) {
    const angle = (Math.PI * 2 * i) / crackCount + rand(-0.3, 0.3);
    const length = surface === SurfaceType.Glass ? rand(16, 30) : rand(12, 28);
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(angle) * 4, cy + Math.sin(angle) * 4);

    const mx = cx + Math.cos(angle) * length * 0.5 + rand(-3, 3);
    const my = cy + Math.sin(angle) * length * 0.5 + rand(-3, 3);
    const ex = cx + Math.cos(angle) * length + rand(-2, 2);
    const ey = cy + Math.sin(angle) * length + rand(-2, 2);
    ctx.quadraticCurveTo(mx, my, ex, ey);
    ctx.stroke();
  }

  // Glass gets concentric ring cracks
  if (surface === SurfaceType.Glass) {
    ctx.strokeStyle = colors.crack.replace(/[\d.]+\)$/, '0.4)');
    ctx.lineWidth = 0.5;
    for (let r = 8; r < 24; r += rand(5, 9)) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, rand(0, Math.PI), rand(Math.PI, Math.PI * 2));
      ctx.stroke();
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  effectTextureCache.set(key, texture);
  // BUG-05: Release canvas DOM element after GPU upload — pixel data already copied
  (texture.source as { data: HTMLCanvasElement | null }).data = null;
  return texture;
}

// ---------------------------------------------------------------------------
// 2. Bullet Hole
// ---------------------------------------------------------------------------

// Shared geometry for all bullet hole decals (single allocation)
const bulletHoleGeometry = new THREE.PlaneGeometry(0.12, 0.12);

const bulletHoles: Array<{ mesh: THREE.Mesh; createdAt: number }> = [];
const MAX_BULLET_HOLES = 50;

export function createBulletHoleTexture(): THREE.Texture {
  const key = '_bullethole_';
  const cached = effectTextureCache.get(key);
  if (cached) return cached;

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;

  // Dark center hole
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx * 0.9);
  grad.addColorStop(0, 'rgba(15,12,10,0.95)');
  grad.addColorStop(0.2, 'rgba(30,25,20,0.85)');
  grad.addColorStop(0.35, 'rgba(60,55,45,0.5)');
  grad.addColorStop(0.6, 'rgba(80,75,65,0.2)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // Crack lines radiating from center
  ctx.strokeStyle = 'rgba(20,18,15,0.6)';
  ctx.lineWidth = 1;
  const crackCount = 4 + Math.floor(Math.random() * 4);
  for (let i = 0; i < crackCount; i++) {
    const angle = (Math.PI * 2 * i) / crackCount + rand(-0.3, 0.3);
    const length = rand(12, 28);
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(angle) * 4, cy + Math.sin(angle) * 4);

    // Jagged crack with midpoint displacement
    const mx = cx + Math.cos(angle) * length * 0.5 + rand(-3, 3);
    const my = cy + Math.sin(angle) * length * 0.5 + rand(-3, 3);
    const ex = cx + Math.cos(angle) * length + rand(-2, 2);
    const ey = cy + Math.sin(angle) * length + rand(-2, 2);
    ctx.quadraticCurveTo(mx, my, ex, ey);
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  effectTextureCache.set(key, texture);
  return texture;
}

export function spawnBulletHole(
  position: THREE.Vector3,
  normal: THREE.Vector3,
  scene: THREE.Scene,
  surfaceType?: SurfaceType,
): void {
  const surface = surfaceType ?? SurfaceType.Concrete;

  // Evict oldest if at capacity (batch evict to avoid repeated scene.remove)
  if (bulletHoles.length >= MAX_BULLET_HOLES) {
    const oldest = bulletHoles.shift()!;
    // Dispose material only — geometry is shared across all bullet holes
    scene.remove(oldest.mesh);
    if (oldest.mesh instanceof THREE.Mesh && oldest.mesh.material) {
      (oldest.mesh.material as THREE.Material).dispose();
    }
  }

  // Decal quad aligned to surface normal — use surface-specific texture
  const holeTex = createSurfaceBulletHoleTexture(surface);
  const mat = new THREE.MeshBasicMaterial({
    map: holeTex,
    transparent: true,
    opacity: 1,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });

  const mesh = new THREE.Mesh(bulletHoleGeometry, mat);
  mesh.userData.isEffect = true;
  mesh.position.copy(position).addScaledVector(normal, 0.005);

  const target = position.clone().add(normal);
  mesh.lookAt(target);

  markEffect(mesh);
  scene.add(mesh);

  const entry = { mesh, createdAt: performance.now() };
  bulletHoles.push(entry);

  // Fade-out after 15s — use setTimeout instead of RAF (no per-frame cost for 15s!)
  setTimeout(() => {
    // BUG-09: Scene may be disposed before timeout fires — check mesh still in graph
    if (!mesh.parent) {
      const idx = bulletHoles.indexOf(entry);
      if (idx !== -1) bulletHoles.splice(idx, 1);
      mat.dispose();
      return;
    }
    // Start fade animation only when it's time
    const fadeStart = performance.now();
    const FADE = 2000;
    addEffect({
      update(now) {
        const fadeT = Math.min((now - fadeStart) / FADE, 1);
        mat.opacity = 1 - fadeT;
        if (fadeT >= 1) {
          const idx = bulletHoles.indexOf(entry);
          if (idx !== -1) bulletHoles.splice(idx, 1);
          // Dispose material only — geometry is shared across all bullet holes
          if (mesh.parent) mesh.parent.remove(mesh);
          mat.dispose();
          return false;
        }
        return true;
      },
    });
  }, 15000);

  // --- Surface-appropriate impact particles ---
  const cfg = SURFACE_PARTICLE_CONFIGS[surface];
  const impactTex = createSpriteTexture(32, cfg.colors[0], cfg.colors[1], cfg.spriteType);
  const impactCount = cfg.count[0] + Math.floor(Math.random() * (cfg.count[1] - cfg.count[0] + 1));
  const impactParticles: Array<{ sprite: THREE.Sprite; velocity: THREE.Vector3; baseScale: number }> = [];

  for (let i = 0; i < impactCount; i++) {
    const dmat = new THREE.SpriteMaterial({
      map: impactTex,
      transparent: true,
      opacity: cfg.opacityStart,
      depthWrite: false,
      blending: cfg.blending,
    });
    const sprite = new THREE.Sprite(dmat);
    const initScale = rand(cfg.scaleRange[0], cfg.scaleRange[1]);
    sprite.scale.set(initScale, initScale, initScale);
    sprite.position.copy(position);
    markEffect(sprite);
    scene.add(sprite);

    // Velocity: predominantly along surface normal with tangential spread
    const tangent = new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-1, 1))
      .sub(normal.clone().multiplyScalar(new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-1, 1)).dot(normal)))
      .normalize()
      .multiplyScalar(rand(cfg.speed[0], cfg.speed[1]) * 0.4);
    tangent.add(normal.clone().multiplyScalar(rand(cfg.speed[0], cfg.speed[1])));

    impactParticles.push({ sprite, velocity: tangent, baseScale: initScale });
  }

  const impactStart = performance.now();
  let impactLastTime = impactStart;
  const IMPACT_DURATION = cfg.durationMs;

  addEffect({
    update(now) {
      const dt = Math.min((now - impactLastTime) / 1000, 0.05);
      impactLastTime = now;
      const elapsed = now - impactStart;
      const t = Math.min(elapsed / IMPACT_DURATION, 1);

      for (const d of impactParticles) {
        d.velocity.y += cfg.gravity * dt;
        d.sprite.position.addScaledVector(d.velocity, dt);
        const dmat = d.sprite.material as THREE.SpriteMaterial;
        dmat.opacity = cfg.opacityStart * (1 - t);
        // Compute scale from the original baseScale so scaleGrow is not
        // accumulated across frames.
        const s = d.baseScale + t * cfg.scaleGrow;
        d.sprite.scale.set(Math.max(0.001, s), Math.max(0.001, s), Math.max(0.001, s));
      }

      if (t >= 1) {
        for (const d of impactParticles) {
          if (d.sprite.parent) disposeMesh(d.sprite, d.sprite.parent as THREE.Scene);
        }
        return false;
      }
      return true;
    },
  });
}

// ---------------------------------------------------------------------------
// 3. Smoke
// ---------------------------------------------------------------------------

export function spawnSmoke(
  position: THREE.Vector3,
  scene: THREE.Scene,
  duration: number = 5000,
): void {
  const smokeTex = createSpriteTexture(64, 'rgba(180,175,165,0.7)', 'rgba(130,125,115,0.3)', 'smoke');
  const COUNT = 10 + Math.floor(Math.random() * 4); // reduced from 15-20
  const FADE_OUT = 1000;

  const particles: Array<{
    sprite: THREE.Sprite;
    velocity: THREE.Vector3;
    baseScale: number;
    turbulenceOffset: number;
  }> = [];

  for (let i = 0; i < COUNT; i++) {
    const mat = new THREE.SpriteMaterial({
      map: smokeTex,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });
    const sprite = new THREE.Sprite(mat);

    const baseScale = rand(0.3, 0.6);
    sprite.scale.set(baseScale, baseScale, baseScale);
    sprite.position.copy(position);
    sprite.position.x += rand(-0.3, 0.3);
    sprite.position.y += rand(0, 0.2);
    sprite.position.z += rand(-0.3, 0.3);
    markEffect(sprite);
    scene.add(sprite);

    particles.push({
      sprite,
      velocity: new THREE.Vector3(
        rand(-0.8, 0.8),
        rand(0.5, 1.5),
        rand(-0.8, 0.8),
      ),
      baseScale,
      turbulenceOffset: rand(0, Math.PI * 2),
    });
  }

  const start = performance.now();
  let smokeLastTime = start;

  addEffect({
    update(now) {
      const dt = Math.min((now - smokeLastTime) / 1000, 0.05);
      smokeLastTime = now;
      const elapsed = now - start;
      if (elapsed >= duration) {
        for (const p of particles) disposeMesh(p.sprite, scene);
        return false;
      }

      const globalT = elapsed / duration;

      for (const p of particles) {
        const turbTime = elapsed * 0.001 + p.turbulenceOffset;
        p.velocity.x += Math.sin(turbTime * 2.3) * 0.02;
        p.velocity.z += Math.cos(turbTime * 1.7) * 0.02;

        p.sprite.position.addScaledVector(p.velocity, dt);
        p.velocity.multiplyScalar(0.995);

        const expansion = 1 + globalT * 1.5;
        const scale = p.baseScale * expansion;
        p.sprite.scale.set(scale, scale, scale);

        const mat = p.sprite.material as THREE.SpriteMaterial;
        const fadeInT = Math.min(elapsed / 300, 1);
        const remaining = duration - elapsed;
        const fadeOutT = remaining < FADE_OUT ? remaining / FADE_OUT : 1;
        mat.opacity = 0.55 * fadeInT * fadeOutT;
      }

      return true;
    },
  });
}
